import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Layers, ArrowRight, GraduationCap, Wrench, Target,
} from 'lucide-react';

const AboutPage = () => (
  <div data-testid="about-page">
    <div className="bg-gradient-to-br from-[#0B172A] to-slate-800 text-white px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3">About This Platform</h1>
        <p className="text-slate-300 text-lg max-w-2xl">
          Who it's for, how it's structured, and how to get the most out of it.
        </p>
      </div>
    </div>

    <div className="px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto space-y-12">

      {/* Who it's for */}
      <section>
        <h2 className="text-2xl font-semibold font-heading text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <GraduationCap className="h-6 w-6 text-teal-600" />
          Who This Is For
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { title: 'EIT Candidates', desc: 'Studying for the PE exam? Start with Foundation → Per-Unit → Fault Analysis. These topics are heavily tested.' },
            { title: 'Distribution Engineers', desc: 'Working on feeder design? Use Distribution Design, Protection, and the interactive calculators for quick field checks.' },
            { title: 'Engineering Students', desc: 'Learning power systems for the first time? Follow the curriculum map from top to bottom.' },
          ].map(({ title, desc }) => (
            <div key={title} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2 font-heading">{title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Module structure */}
      <section>
        <h2 className="text-2xl font-semibold font-heading text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Layers className="h-6 w-6 text-teal-600" />
          How Each Module Is Structured
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
          Every module follows the same 9-section template, from real-world hook to self-check quiz.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { n: 1, title: 'Why It Matters', desc: 'Real utility scenario that motivates the topic.' },
            { n: 2, title: 'Concept Visual', desc: 'Diagram or animation showing the concept intuitively.' },
            { n: 3, title: 'Core Theory', desc: 'Definitions, derivations, key assumptions.' },
            { n: 4, title: 'Formula Sheet', desc: 'Boxed KaTeX formulas with variable definitions.' },
            { n: 5, title: 'Common Mistakes', desc: 'Top 5 errors engineers make with this topic.' },
            { n: 6, title: 'Worked Example', desc: 'Realistic utility numbers, solved step-by-step.' },
            { n: 7, title: 'Interactive Tool', desc: 'Calculator or simulator with teaching tooltips.' },
            { n: 8, title: 'Self-Check Quiz', desc: '5 multiple-choice questions to test retention.' },
            { n: 9, title: 'Connected Topics', desc: 'Links to upstream and downstream concepts.' },
          ].map(({ n, title, desc }) => (
            <div key={n} className="flex items-start gap-3 py-2">
              <div className="section-badge flex-shrink-0">{n}</div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white text-sm">{title}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tooltip system */}
      <section>
        <h2 className="text-2xl font-semibold font-heading text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Wrench className="h-6 w-6 text-teal-600" />
          Teaching Tooltips
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
          Every slider, input, and dropdown has a teaching tooltip that opens on hover. Each tooltip follows a 4-part format:
        </p>
        <div className="bg-slate-900 rounded-lg p-5 text-sm space-y-3">
          <div><span className="text-teal-400 font-semibold">What this is: </span><span className="text-slate-300">Plain-English explanation of what the parameter means physically in a real power system.</span></div>
          <div><span className="text-amber-400 font-semibold">Try this: </span><span className="text-slate-300">An action prompt with cause-and-effect guidance — what to change and what to watch.</span></div>
          <div><span className="text-sky-400 font-semibold">Why it matters: </span><span className="text-slate-300">One sentence connecting this value to a real engineering decision.</span></div>
          <div><span className="text-purple-400 font-semibold">Connected to: </span><span className="text-slate-400">The outputs and diagrams that visually respond when this value changes.</span></div>
        </div>
      </section>

      {/* Learning path */}
      <section>
        <h2 className="text-2xl font-semibold font-heading text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Target className="h-6 w-6 text-teal-600" />
          Suggested Learning Paths
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'New to Power Systems', path: 'Foundation → Transformers → Per-Unit → Sym. Components → Sequence Networks → Fault Analysis' },
            { title: 'PE Exam Prep', path: 'Per-Unit → Sym. Components → Sequence Networks → Fault Analysis → Protection' },
            { title: 'Distribution Design Focus', path: 'Foundation → Transformers → Per-Unit → Distribution Design + Protection' },
            { title: 'Quick Reference', path: 'Go directly to the Calculators Hub for any specific tool.' },
          ].map(({ title, path }) => (
            <div key={title} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1.5 font-heading">{title}</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">{path}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-slate-200 dark:border-slate-800 pt-8 text-center">
        <p className="text-slate-600 dark:text-slate-400 mb-4">Ready to start learning?</p>
        <Link
          to="/per-unit"
          data-testid="about-start-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-medium rounded-md transition-colors"
        >
          Start with Per-Unit Analysis
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default AboutPage;
